<template>
  <a-form layout="vertical">
    <a-form-item :label="t('shell.account.currentEmail')">
      <a-input :value="currentEmail || t('shell.account.emailUnset')" disabled />
    </a-form-item>
    <a-form-item :label="t('shell.account.newEmail')">
      <a-input v-model:value="form.email" :placeholder="emailPlaceholder" />
    </a-form-item>
    <a-form-item :label="t('shell.account.emailCode')" required>
      <div class="flex items-center gap-2">
        <a-input
          v-model:value="form.emailCode"
          :placeholder="t('shell.account.emailCodePlaceholder')"
        />
        <a-button
          class="shrink-0"
          :loading="sending"
          :disabled="countdown > 0 || !form.email.trim()"
          @click="handleSendCode"
        >
          {{
            countdown > 0
              ? t('shell.account.codeResend', { seconds: countdown })
              : t('shell.account.sendCode')
          }}
        </a-button>
      </div>
    </a-form-item>
    <a-form-item>
      <a-button
        type="primary"
        :loading="saving"
        :disabled="!form.email.trim() || !form.emailCode.trim()"
        @click="handleSave"
      >
        {{ t('shell.account.saveEmail') }}
      </a-button>
    </a-form-item>
  </a-form>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { getRegisterInfo } from '../../api/auth'
import { sendEmailCode, updateEmail } from '../../api/user/setting'
import { useI18n } from '../../composables/useI18n'

defineProps<{ currentEmail: string }>()
const emit = defineEmits<{ updated: [value: string] }>()
const { t } = useI18n()

const form = reactive({ email: '', emailCode: '' })
const saving = ref(false)
const sending = ref(false)
const countdown = ref(0)
let timer: ReturnType<typeof setInterval> | null = null
// 仅当系统开启找回密码时，邮箱才用于找回密码；否则仅作为普通联系方式
const recoveryEnabled = ref(false)

// 找回密码可用时提示用途，单用户系统等未开启时直接提示输入新邮箱
const emailPlaceholder = computed(() =>
  recoveryEnabled.value
    ? t('shell.account.newEmailPlaceholder')
    : t('shell.account.newEmailPlaceholderPlain'),
)

function stopCountdown() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  countdown.value = 0
}

function startCountdown() {
  stopCountdown()
  countdown.value = 60
  timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) stopCountdown()
  }, 1000)
}

onMounted(async () => {
  try {
    const info = await getRegisterInfo()
    recoveryEnabled.value = info.registerEnable
  } catch {
    recoveryEnabled.value = false
  }
})

onUnmounted(stopCountdown)

async function handleSendCode() {
  const email = form.email.trim()
  if (!email) {
    message.warning(t('shell.account.emailRequired'))
    return
  }
  sending.value = true
  try {
    await sendEmailCode(email)
    message.success(t('shell.account.codeSent'))
    startCountdown()
  } catch (e: any) {
    message.error(e?.message || t('shell.account.codeSendFailed'))
  } finally {
    sending.value = false
  }
}

async function handleSave() {
  const email = form.email.trim()
  const emailCode = form.emailCode.trim()
  if (!email) {
    message.warning(t('shell.account.emailRequired'))
    return
  }
  if (!emailCode) {
    message.warning(t('shell.account.emailCodeRequired'))
    return
  }
  saving.value = true
  try {
    await updateEmail(email, emailCode)
    message.success(t('shell.account.emailSuccess'))
    emit('updated', email)
    form.email = ''
    form.emailCode = ''
    stopCountdown()
  } catch (e: any) {
    message.error(e?.message || t('shell.account.updateFailed'))
  } finally {
    saving.value = false
  }
}
</script>
